/**
 * Générateur — Automatisme : comparer deux fractions :
 * 2/7 et 5/7 ; 8/12 et 8/21 ; 3/4 et 7/18 ; 8/3 et 6/7.
 * Attendu ID : 1052 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8). Cadrage rédigé
 * par le sous-agent programme-maths, passé à l'épreuve de Musk et validé
 * par le prof le 2026-09-08.
 *
 * Le geste : devant deux fractions, reconnaître EN UN REGARD lequel des
 * quatre cas s'applique et écrire < ou > sans rien calculer. Les quatre
 * exemples du programme sont quatre cas, un geste chacun :
 *   meme_den : 2/7 et 5/7, même dénominateur, on compare les numérateurs ;
 *   meme_num : 8/12 et 8/21, même numérateur, plus de parts au bas = parts
 *              plus petites ;
 *   moitie   : 3/4 et 7/18, l'une sous 1/2, l'autre au-dessus ;
 *   un       : 8/3 et 6/7, l'une dépasse 1, l'autre non.
 * Jamais de dénominateur commun, jamais d'écriture décimale (objectif de
 * l'année, gen_923), jamais = (fractions égales : 1051), jamais 3 fractions.
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash                     (4 lignes, une par cas, saisie du
 *                                      seul symbole < ou >)
 *   - Laquelle est la plus grande ?   (QCM « une seule comparaison juste »
 *                                      4 choix, ajouté le 2026-09-09 ;
 *                                      Vrai/Faux 3 inégalités)
 * Supprimés à l'épreuve de Musk : la carte « repère 1/2 et repère 1 »
 * (Classer), le QCM « pourquoi », les affirmations d'égalité au Vrai/Faux,
 * puis à la revue le QCM « plus grande que t ? » lui-même : avec trois
 * candidates dont deux partagent le bas ou le haut de la cible, la clé se
 * repérait par « le plus petit dénominateur » deux fois sur trois (mesuré),
 * fuite de structure, pas de réglage ; la Série flash et le Vrai/Faux font
 * tout le travail.
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Dénominateurs 2 à 12 (meme_den, un), jusqu'à 21 (meme_num, moitie) ;
 * numérateurs ≤ 20. PURETÉ DU CAS : meme_den et meme_num toutes deux < 1
 * (les fractions > 1 sont réservées au cas un) ; moitie : toutes deux < 1, l'une
 * strictement sous 1/2, l'autre strictement au-dessus, hauts et bas
 * différents ; un : une seule > 1 (et < 2), hauts et bas différents. Jamais
 * deux fractions égales, jamais égale à 1, jamais égale à 1/2. Écart d'au
 * moins 2 entre les deux dénominateurs de meme_num (gen_628).
 * CAS DISCRIMINANT (une fois par série flash) : la plus grande a le plus
 * petit numérateur (3/4 et 7/18 ; 5/4 et 6/7). Avec 8/3 et 6/7, « le plus
 * grand haut gagne » donne la bonne réponse ; il faut un couple où il la
 * donne fausse.
 * COUPLES SACRIFIÉS (la fiche, une page par cas, 2026-09-09) : 2/7 et 5/7,
 * 8/12 et 8/21, 2/5 et 5/8, 4/3 et 5/6 ne sortent jamais ensemble d'une
 * carte, auto-testé. Les exemples du programme 2/7 et 5/7, 8/12 et 8/21
 * vivent donc dans la fiche, pas dans les tirages ; 8/3 et 6/7 est hors
 * domaine (les fractions du cas « un » restent sous 2).
 *
 * ── Distracteurs (familles nommées, cohérentes entre les cartes) ─────────
 *   numerateur_seul   : il compare les hauts comme des entiers (3/4 < 7/18)
 *                       — L'ERREUR PRINCIPALE ;
 *   denominateur_grand: « plus de parts, c'est plus » (8/12 < 8/21) ;
 *   a_l_envers        : la règle du bas appliquée au haut (5/7 < 2/7) ;
 *   fraction_sous_1   : « une fraction, c'est toujours moins que 1 » ;
 *   moitie_oubliee    : le repère 1/2 mal placé (7/18 jugé > 1/2).
 * Le sens du symbole (< pour >) n'est pas une conception sur les fractions :
 * la saisie stricte le compte faux, aucun distracteur ne le porte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1052;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_PLUS = 'Laquelle est la plus grande ?';

// ─── Le domaine ───────────────────────────────────────────────────────────

const DEN_SIMPLE = 12;
const DEN_LARGE = 21;
const NUM_MAX = 20;
const CAS = ['meme_den', 'meme_num', 'moitie', 'un'];

/** Les couples des quatre pages du coup de pouce, sacrifiés, un par cas. */
export const COUPLES_SACRIFIES = [
    [{ a: 2, b: 7 }, { a: 5, b: 7 }],
    [{ a: 8, b: 12 }, { a: 8, b: 21 }],
    [{ a: 2, b: 5 }, { a: 5, b: 8 }],
    [{ a: 4, b: 3 }, { a: 5, b: 6 }],
];
const memeFraction = (f, g) => f.a === g.a && f.b === g.b;
const estSacrifie = (f, g) => COUPLES_SACRIFIES.some(([u, v]) => (memeFraction(f, u) && memeFraction(g, v))
    || (memeFraction(f, v) && memeFraction(g, u)));

/** Signe de f − g (produit en croix). */
export const signe = (f, g) => Math.sign(f.a * g.b - g.a * f.b);
const sousMoitie = (f) => 2 * f.a < f.b;
const surMoitieSous1 = (f) => 2 * f.a > f.b && f.a < f.b;

/**
 * Un couple d'un cas donné : { x, y, cas } avec x > y, tous les invariants de
 * pureté vérifiés ; null si le tirage échoue (l'appelant retire).
 */
export const tirerCouple = (cas, { discriminant = null } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        let x;
        let y;
        // meme_den et meme_num : toutes deux SOUS 1 (épreuve de Musk, revue prof
        // 2026-09-08 : le sous-cas « toutes deux plus grandes que 1 » donnait
        // 11/2 face à 11/9 sans rien ajouter au geste ; les fractions > 1 sont
        // réservées au cas « un », comme les exemples du programme)
        if (cas === 'meme_den') {
            const b = randInt(3, DEN_SIMPLE);
            const a = randInt(1, b - 1);
            const c = randInt(1, b - 1);
            if (a === c) continue;
            x = { a: Math.max(a, c), b };
            y = { a: Math.min(a, c), b };
        } else if (cas === 'meme_num') {
            const a = randInt(1, DEN_LARGE - 3);
            const b = randInt(a + 1, DEN_LARGE);
            const c = randInt(a + 1, DEN_LARGE);
            if (Math.abs(b - c) < 2) continue;
            x = { a, b: Math.min(b, c) };
            y = { a, b: Math.max(b, c) };
        } else if (cas === 'moitie') {
            const q = randInt(3, DEN_LARGE);
            const p = randInt(1, Math.floor((q - 1) / 2));
            const s = randInt(3, DEN_LARGE);
            const r = randInt(Math.floor(s / 2) + 1, s - 1);
            y = { a: p, b: q };
            x = { a: r, b: s };
            if (!sousMoitie(y) || !surMoitieSous1(x) || x.a === y.a || x.b === y.b || x.a > NUM_MAX) continue;
        } else {
            const b = randInt(2, DEN_SIMPLE);
            const a = randInt(b + 1, 2 * b - 1);
            const d = randInt(2, DEN_SIMPLE);
            const c = randInt(1, d - 1);
            x = { a, b };
            y = { a: c, b: d };
            if (x.a === y.a || x.b === y.b || x.a > NUM_MAX) continue;
        }
        if (signe(x, y) <= 0 || estSacrifie(x, y)) continue;
        if (x.a === x.b || y.a === y.b || 2 * x.a === x.b || 2 * y.a === y.b) continue;
        const estDiscriminant = x.a < y.a;
        if (discriminant === true && !estDiscriminant) continue;
        if (discriminant === false && estDiscriminant) continue;
        return { x, y, cas, discriminant: estDiscriminant };
    }
    return null;
};

/** Replis déterministes vérifiés, un par cas, hors couples sacrifiés, le discriminant en moitie et en un. */
export const REPLIS = {
    meme_den: { x: { a: 7, b: 9 }, y: { a: 4, b: 9 }, cas: 'meme_den', discriminant: false },
    meme_num: { x: { a: 5, b: 9 }, y: { a: 5, b: 14 }, cas: 'meme_num', discriminant: false },
    moitie: { x: { a: 3, b: 4 }, y: { a: 7, b: 18 }, cas: 'moitie', discriminant: true },
    un: { x: { a: 5, b: 4 }, y: { a: 6, b: 7 }, cas: 'un', discriminant: true },
};
export const coupleOuRepli = (cas, contraintes) => tirerCouple(cas, contraintes) || REPLIS[cas];

// ─── Écritures ────────────────────────────────────────────────────────────

export const fracTex = (f) => `\\dfrac{${f.a}}{${f.b}}`;

export const EXPLICATIONS = {
    numerateur_seul: 'Comparer les numérateurs seuls ne marche que si les parts sont les mêmes : je regarde d\'abord le dénominateur.',
    denominateur_grand: 'Plus le dénominateur est grand, plus les parts sont petites : avec le même nombre de parts, c\'est moins.',
    a_l_envers: 'Avec le même dénominateur, les parts sont les mêmes : plus de parts, c\'est plus.',
    fraction_sous_1: 'Une fraction dont le haut dépasse le bas est plus grande que 1.',
    moitie_oubliee: 'La moitié de 18, c\'est 9 : 7 est en dessous, donc 7/18 est plus petit que 1/2.',
    regle: 'Même bas : je compare les hauts. Même haut : le plus petit bas gagne. Sinon, je regarde 1/2 ou 1.',
};

// ─── Carte 1 : Série flash (4 lignes, saisie du symbole) ──────────────────

const CONSIGNE_FLASH = '<p><b>Complète chaque comparaison par &lt; ou &gt;.</b></p>';

/** Une ligne « f ⬚ g », le trou porte le symbole attendu. */
export const ligneFlash = (l) => {
    const [gauche, droite] = l.grandAGauche ? [l.x, l.y] : [l.y, l.x];
    const sym = l.grandAGauche ? '>' : '<';
    return `${fracTex(gauche)} \\;\\; {{${sym}}} \\;\\; ${fracTex(droite)}`;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        // un couple par cas ; le discriminant porté par moitie ou par un
        const porteur = randomChoice(['moitie', 'un']);
        const lignes = CAS.map((cas) => coupleOuRepli(cas, cas === porteur ? { discriminant: true } : {}));
        if (lignes.some((l) => !l)) continue;
        if (!lignes.some((l) => l.discriminant)) continue;
        // la plus grande à gauche exactement deux fois sur quatre
        const cotes = shuffle([true, true, false, false]);
        const items = shuffle(lignes).map((l, i) => ({ ...l, grandAGauche: cotes[i] }));
        const phrase = [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');
        const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        payload._meta.params = { items: items.map(({ x, y, cas, discriminant, grandAGauche }) => ({ x, y, cas, discriminant, grandAGauche })) };
        return payload;
    }
    const items = CAS.map((cas, i) => ({ ...REPLIS[cas], grandAGauche: i % 2 === 0 }));
    const phrase = [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');
    const payload = makeTexteTrou(phrase, CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2 : Vrai ou faux (3 inégalités) ────────────────────────────────

/** Le code de la conception qui rendrait vraie l'inégalité FAUSSE « y > x » (ou « x < y »). */
export const codeFaux = (l) => {
    if (l.cas === 'meme_den') return 'a_l_envers';
    if (l.cas === 'meme_num') return 'denominateur_grand';
    if (l.cas === 'un') return l.discriminant ? 'numerateur_seul' : 'fraction_sous_1';
    return l.discriminant ? 'numerateur_seul' : 'moitie_oubliee';
};

/** « f < g » ou « f > g » en KaTeX, vraie ou fausse selon `vraie`. */
export const affirmation = (l, vraie) => {
    const gaucheGrand = randomChoice([true, false]);
    const [g, d] = gaucheGrand ? [l.x, l.y] : [l.y, l.x];
    const symVrai = gaucheGrand ? '>' : '<';
    const sym = vraie ? symVrai : (symVrai === '>' ? '<' : '>');
    return { ligne: tex(`${fracTex(g)} ${sym} ${fracTex(d)}`), code: vraie ? null : codeFaux(l) };
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const cas = shuffle([...CAS]).slice(0, 3);
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        // au moins une fausse que « numérateur seul » jugerait vraie : un
        // couple discriminant (moitie ou un) déclaré à l'envers
        const iFaux = verites.indexOf(false);
        const couples = cas.map((c, i) => {
            const contrainte = i === iFaux && (c === 'moitie' || c === 'un') ? { discriminant: true } : {};
            return coupleOuRepli(c, contrainte);
        });
        if (couples.some((c) => !c)) continue;
        if (!couples.some((c, i) => !verites[i] && c.discriminant)) continue;
        const affs = couples.map((c, i) => affirmation(c, verites[i]));
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_PLUS));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { couples: couples.map(({ x, y, cas: c, discriminant }) => ({ x, y, cas: c, discriminant })), verites, distracteurs: affs.filter((a) => a.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe : 7/9 > 4/9 vrai ; 7/18 > 3/4 faux (numerateur_seul) ; 5/9 < 5/14 faux (denominateur_grand).
    const couples = [REPLIS.meme_den, REPLIS.moitie, REPLIS.meme_num];
    const lignes = [
        tex(`${fracTex({ a: 7, b: 9 })} > ${fracTex({ a: 4, b: 9 })}`),
        tex(`${fracTex({ a: 7, b: 18 })} > ${fracTex({ a: 3, b: 4 })}`),
        tex(`${fracTex({ a: 5, b: 9 })} < ${fracTex({ a: 5, b: 14 })}`),
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_PLUS));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { couples, verites: [true, false, false], distracteurs: [{ code: 'numerateur_seul' }, { code: 'denominateur_grand' }] };
    return payload;
};

// ─── Carte 2, format QCM : une seule comparaison juste ────────────────────
// Ajouté le 2026-09-09 (règle générale : un QCM par attendu, seul format
// dépouillable en mode classe QR code). L'ancien QCM « laquelle est plus
// grande que t ? » (trois candidates) avait été supprimé à la revue : la clé
// se lisait au plus petit dénominateur deux fois sur trois. Ici les quatre
// choix sont quatre comparaisons écrites, une par cas, le cas vrai tiré
// uniformément ; les trois fausses portent la conception de leur cas
// (codeFaux), le sens du symbole tiré ligne par ligne : la clé est un CAS,
// pas une fraction, aucune position ne la porte. Mesuré à la revue.

const CONSIGNE_QCM = '<p><b>Une seule de ces comparaisons est juste. Laquelle ?</b></p>';

const genQcm = () => {
    for (let essai = 0; essai < 120; essai++) {
        // moitie et un toujours DISCRIMINANTS (la plus grande a le plus petit
        // numérateur) : mesuré à la revue, sans cela « je compare les numérateurs
        // seuls » désignait la clé 63 % du temps (les fausses non discriminantes
        // contredisent les numérateurs, la clé les suit) ; avec, cette lecture
        // ne trouve la clé que dans un tirage sur douze, elle n'aide plus.
        const couples = CAS.map((cas) => coupleOuRepli(cas, cas === 'moitie' || cas === 'un' ? { discriminant: true } : {}));
        const iVrai = randInt(0, 3);
        const affs = couples.map((c, i) => affirmation(c, i === iVrai));
        const choix = affs.map((a) => a.ligne);
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, choix[iVrai], CONSIGNE_QCM, opts(L_PLUS));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = {
            couples: couples.map(({ x, y, cas, discriminant }) => ({ x, y, cas, discriminant })),
            casVrai: couples[iVrai].cas,
            distracteurs: affs.filter((a) => a.code).map(({ code }) => ({ code })),
        };
        return payload;
    }
    // Repli déterministe : 7/9 > 4/9 vrai ; 7/18 > 3/4 faux (numerateur_seul) ; 5/9 < 5/14 faux (denominateur_grand) ; 6/7 > 5/4 faux (numerateur_seul).
    const couples = [REPLIS.meme_den, REPLIS.moitie, REPLIS.meme_num, REPLIS.un];
    const choix = [
        tex(`${fracTex({ a: 7, b: 9 })} > ${fracTex({ a: 4, b: 9 })}`),
        tex(`${fracTex({ a: 7, b: 18 })} > ${fracTex({ a: 3, b: 4 })}`),
        tex(`${fracTex({ a: 5, b: 9 })} < ${fracTex({ a: 5, b: 14 })}`),
        tex(`${fracTex({ a: 6, b: 7 })} > ${fracTex({ a: 5, b: 4 })}`),
    ];
    const payload = makeQcm(choix, choix[0], CONSIGNE_QCM, opts(L_PLUS));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { couples, casVrai: 'meme_den', distracteurs: [{ code: 'numerateur_seul' }, { code: 'denominateur_grand' }, { code: 'numerateur_seul' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre cas purs (même bas, même haut, repère 1/2, repère 1) ; jamais deux fractions égales, jamais égale à 1 ni à 1/2 ; les quatre couples de la fiche (2/7 et 5/7, 8/12 et 8/21, 2/5 et 5/8, 4/3 et 5/6) exclus',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    plus_qcm_unique: 'quatre comparaisons écrites, une par cas, une seule vraie (cas vrai uniforme) ; moitie et un toujours discriminants, donc les fausses portent a_l_envers, denominateur_grand et numerateur_seul (fraction_sous_1 et moitie_oubliee vivent au Vrai/Faux) ; symbole tiré ligne par ligne ; mesuré : « numérateurs seuls » 8 %, « même dénominateur » 25 %, « ligne avec > » 24 %, « symbole rare » 12 % ; ajouté le 2026-09-09 (un QCM par attendu), l\'ancien QCM à trois candidates ayant été supprimé à la revue (clé = plus petit dénominateur 74 %)',
    plus_tableau_vf: 'trois inégalités, un cas chacune (trois cas sur quatre), 1 ou 2 vraies ; une fausse au moins que « numérateur seul » jugerait vraie',
    flash_texte_trou_saisie: 'quatre lignes, une par cas, le symbole en trou (saisie stricte) ; une ligne discriminante ; la plus grande à gauche deux fois sur quatre',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    plus_qcm_unique: genQcm,
    plus_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'plus', label: L_PLUS, formatsList: [
        { key: 'plus_qcm_unique', label: 'QCM' },
        { key: 'plus_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
